/**
 * components/ui.tsx — the primitives, and the states each one is required to have.
 *
 * The design system's most load-bearing claim is that the happy path is rarely where two applications
 * diverge — the blank rectangle is. So the four states beyond the happy path (loading, empty, error,
 * permission-denied) are components here rather than an instruction each screen follows in its own way,
 * and `Table` is written so that a screen CANNOT ship a table that does not sort: the sort is not a prop
 * to enable, it is what the component does.
 *
 * Two things in this file are capability floors rather than features, and neither may be quietly dropped:
 *
 *   - **Every table sorts by every column heading**, with `aria-sort` on the `<th>` itself, a real
 *     `<button>` filling the header cell, a visible direction marker beside `aria-sort` rather than
 *     instead of it, a stable order (ties keep their underlying position, so a refetch never reshuffles),
 *     type-aware comparison, and empties grouped at one end in both directions. Below `--bp-md` the table
 *     transforms, and the sort transforms WITH it — dropping to cards is a change of layout, not a
 *     withdrawal of capability.
 *   - **Modal traps focus and gives it back.** It becomes a bottom sheet below `--bp-md` (the stylesheet
 *     does the geometry), closes on Escape and on the scrim, and returns focus to whatever opened it.
 *
 * The trap worth naming: `Table` holds its sort in component state, which is right for a screen that has
 * every row in hand and wrong the moment one paginates on the server — a sort that reorders only the
 * visible page is wrong invisibly. Every current caller passes the whole result set. A screen that starts
 * paging must move the sort into the query, not sort what it happens to be holding.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { compareValues, type SortableValue, type ValueType } from "../lib/format";

/* ---------------------------------------------------------------------------------------------------
 * Hooks
 * ------------------------------------------------------------------------------------------------- */

/**
 * Observe a media query. Used for the layout transforms that cannot be expressed in CSS alone — a table
 * becoming cards is a different DOM, not a different rule set.
 *
 * The initial value is read synchronously so the first paint is already correct; a mobile viewport that
 * renders the desktop table for one frame reflows the whole page.
 *
 * `matchMedia` is feature-detected rather than assumed, and not out of superstition: jsdom does not
 * implement it, so an unguarded call makes every screen containing a table impossible to render in a test
 * — the components with the most behaviour to prove become the ones nothing can prove. Absent the API, the
 * answer is the desktop layout, which is the one that shows every column.
 */
function matchMediaOrNull(query: string): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return null;
  return window.matchMedia(query);
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => matchMediaOrNull(query)?.matches ?? false);

  useEffect(() => {
    const list = matchMediaOrNull(query);
    if (list === null) return;
    const onChange = (event: MediaQueryListEvent): void => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** The one boundary that matters. Mirrors `--bp-md`; the two are changed together or not at all. */
export const MOBILE_QUERY = "(max-width: 767px)";

export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

/**
 * A dismissable panel: click-outside, focus-out, `Escape`, and hash navigation.
 *
 * Written once and given two callers, which is how one knows it is a primitive rather than a helper.
 * `MultiSelect` had the first two behaviours and neither of the last two, and — the part that matters —
 * did not return focus on `Escape`, which `03-components.md` §5.1 requires of every dismissable surface.
 * A keyboard user who dismissed the repository picker was left with focus on a node that had just been
 * unmounted, which in practice means focus on `<body>` and a Tab that starts again from the top of the
 * page.
 *
 * Four listeners, and each is here for a case the others miss:
 *
 *   - **`Escape` is the only path that MOVES focus**, and it moves it back to the trigger. Clicking away
 *     and tabbing out deliberately do not: focus is already where the user just put it, and yanking it
 *     back would fight them.
 *   - The keydown listener is registered in the **bubble** phase on purpose. `Modal` registers its own
 *     with `capture: true` and calls `stopPropagation`, so a modal opened above a panel answers `Escape`
 *     first — the right order, and one that only holds while this listener does not also capture.
 *   - **`focusin`** closes on a Tab out. Without it a panel stays open behind the user as they walk the
 *     rest of the page by keyboard.
 *   - **`hashchange`** is here because every destination in this application is an `<a href="#/…">` and
 *     nothing unmounts the shell on navigation. A panel's own links may additionally close on click, and
 *     both are needed: `onClick` alone misses a browser Back, and `hashchange` alone misses a click on
 *     the address the window is already at, which fires no event at all.
 */
export function useDismissable(
  open: boolean,
  close: () => void,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;

    const inside = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false;
      return triggerRef.current?.contains(target) === true || panelRef.current?.contains(target) === true;
    };

    const onPointerDown = (event: MouseEvent): void => {
      if (!inside(event.target)) close();
    };
    const onFocusIn = (event: FocusEvent): void => {
      if (!inside(event.target)) close();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      // `isComposing` guards an IME: Escape mid-composition cancels the candidate, not the panel.
      if (event.key !== "Escape" || event.isComposing) return;
      close();
      triggerRef.current?.focus();
    };
    const onHashChange = (): void => close();

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("hashchange", onHashChange);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [open, close, triggerRef, panelRef]);
}

/* ---------------------------------------------------------------------------------------------------
 * Button
 * ------------------------------------------------------------------------------------------------- */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps {
  readonly children?: ReactNode;
  readonly onClick?: () => void;
  readonly type?: "button" | "submit";
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly disabled?: boolean;
  /** Reserves the label's width and blocks re-submission — a double tap on a slow link is one write. */
  readonly loading?: boolean;
  readonly block?: boolean;
  /** Required when the button has no visible text. A tooltip is not an accessible name. */
  readonly label?: string;
  readonly title?: string;
  readonly id?: string;
  /**
   * Set on a button that discloses something — a row expansion, a section toggle.
   *
   * `04-data.md` §1.12 requires it of every expansion trigger, and without it a column of buttons all
   * reading "Open" tells a screen reader neither what is open nor which row it belongs to.
   */
  readonly expanded?: boolean;
}

export function Button({
  children,
  onClick,
  type = "button",
  variant = "secondary",
  size = "md",
  disabled = false,
  loading = false,
  block = false,
  label,
  title,
  id,
  expanded,
}: ButtonProps): JSX.Element {
  const classes = ["btn", `btn--${variant}`];
  if (size !== "md") classes.push(`btn--${size}`);
  if (loading) classes.push("btn--loading");
  if (block) classes.push("btn--block");

  return (
    <button
      {...(id === undefined ? {} : { id })}
      type={type}
      className={classes.join(" ")}
      // A loading button is disabled: the label is invisible and a second press would be a second write.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...(label === undefined ? {} : { "aria-label": label })}
      {...(expanded === undefined ? {} : { "aria-expanded": expanded })}
      {...(title === undefined ? {} : { title })}
      {...(onClick === undefined ? {} : { onClick })}
    >
      <span className="btn__label">{children}</span>
      {loading ? (
        <span className="btn__loader">
          <Spinner label="Working" />
        </span>
      ) : null}
    </button>
  );
}

/**
 * A navigation dressed as a button.
 *
 * The sign-in card's action is a real link to `/auth/login`, not a handler that assigns `location`: the
 * provider needs a browser navigation, and a middle-click, a "copy link" or a screen reader's link list
 * should all behave. It wears `.btn` so the size, the states and the coarse-pointer 44px floor apply
 * unchanged. There is deliberately no `loading` and no `disabled` — an anchor can be neither, and an
 * unavailable destination is a link that is not drawn.
 */
export interface ButtonLinkProps {
  readonly children: ReactNode;
  readonly href: string;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly block?: boolean;
}

export function ButtonLink({
  children,
  href,
  variant = "secondary",
  size = "md",
  block = false,
}: ButtonLinkProps): JSX.Element {
  const classes = ["btn", `btn--${variant}`];
  if (size !== "md") classes.push(`btn--${size}`);
  if (block) classes.push("btn--block");

  return (
    <a className={classes.join(" ")} href={href}>
      <span className="btn__label">{children}</span>
    </a>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Fields — label above, control, hint or error below. A placeholder never replaces a label.
 * ------------------------------------------------------------------------------------------------- */

interface FieldFrameProps {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly children: (described: string | undefined) => ReactNode;
  readonly className?: string;
}

/** The label/hint/error scaffolding, shared so every control is described the same way. */
function FieldFrame({ id, label, hint, error, required, children, className }: FieldFrameProps): JSX.Element {
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const described = [errorId, hintId].filter((value): value is string => value !== undefined).join(" ");

  return (
    <div className={className === undefined ? "field" : `field ${className}`}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? (
          <>
            {" "}
            <span className="field__required" aria-hidden="true">
              *
            </span>
            <span className="visually-hidden">(required)</span>
          </>
        ) : null}
      </label>
      {children(described === "" ? undefined : described)}
      {error !== undefined ? (
        <p className="field__error" id={errorId}>
          {/* Not colour alone: the glyph carries the same meaning for anyone who cannot see the red. */}
          <span aria-hidden="true">!</span> {error}
        </p>
      ) : null}
      {hint !== undefined ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: "text" | "email" | "search" | "url";
  /**
   * `decimal` is not a synonym for `numeric` and the difference is not cosmetic: on iOS `numeric` raises a
   * DIGITS-ONLY keypad with no decimal point, so a field whose every valid value has one — a confidence of
   * `0.8` — cannot be filled in at all on a phone. Use `numeric` for whole numbers and `decimal` for
   * anything that may carry a point.
   */
  readonly inputMode?: "text" | "email" | "numeric" | "decimal" | "search" | "url";
  /**
   * `short` narrows the control to about fourteen characters.
   *
   * A field's width is a promise about the value: a box running the width of the form invites a sentence,
   * and a confidence of `0.8` or a reading of `20` sitting in one reads as though something is missing. It
   * is a modifier on the kit rather than a style in a page on purpose — the moment a page hand-rolls its
   * own input width, two screens have two different "narrow" and the system has lost the argument.
   *
   * It narrows the VISUAL width only. The 44px touch floor and the 16px coarse-pointer type size are
   * untouched, both of which are non-deferrable.
   */
  readonly width?: "short";
  readonly autoComplete?: string;
  readonly placeholder?: string;
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly onBlur?: () => void;
}

export function TextField(props: TextFieldProps): JSX.Element {
  const id = useId();
  return (
    <FieldFrame
      id={id}
      label={props.label}
      {...(props.hint === undefined ? {} : { hint: props.hint })}
      {...(props.error === undefined ? {} : { error: props.error })}
      {...(props.required === undefined ? {} : { required: props.required })}
    >
      {(described) => (
        <input
          id={id}
          className={props.width === "short" ? "input input--short" : "input"}
          type={props.type ?? "text"}
          value={props.value}
          onChange={(event: ChangeEvent<HTMLInputElement>) => props.onChange(event.target.value)}
          aria-invalid={props.error === undefined ? undefined : true}
          aria-describedby={described}
          disabled={props.disabled ?? false}
          required={props.required ?? false}
          {...(props.inputMode === undefined ? {} : { inputMode: props.inputMode })}
          {...(props.autoComplete === undefined ? {} : { autoComplete: props.autoComplete })}
          {...(props.placeholder === undefined ? {} : { placeholder: props.placeholder })}
          {...(props.onBlur === undefined ? {} : { onBlur: props.onBlur })}
        />
      )}
    </FieldFrame>
  );
}

export interface TextAreaProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly onBlur?: () => void;
  /** Where the growth stops and the box starts scrolling. Rows, not pixels — it is a measure of text. */
  readonly maxRows?: number;
  /** The height it starts at, before anything is typed. */
  readonly rows?: number;
  /** Passed through to the control, so the browser refuses the overlong value rather than the server. */
  readonly maxLength?: number;
}

/**
 * The multi-line member of §3.1's anatomy — same label/control/hint frame, same `.input` skin.
 *
 * It exists because a single-line `<input>` against a field the server will accept 2,000 characters of
 * is an invitation to write a paragraph the writer cannot see: the text scrolls sideways out of a 36px
 * box, and re-reading what you have written means arrowing through it. A person explaining why a key
 * result slipped will write more than one line.
 *
 * **It auto-grows to a stated maximum and then scrolls**, which is §3.1's own clause and the reason this
 * is not a deviation. The growth is done by measuring rather than by CSS: `field-sizing: content` is the
 * modern answer and is not in the supported matrix, so the height is set from `scrollHeight` — reset to
 * `auto` first, or the box can only ever get taller, never shorter when text is deleted. The ceiling is
 * expressed in ROWS and converted through the computed line height, so it tracks the type scale instead
 * of pinning a pixel value that a token change would silently invalidate.
 */
export function TextArea(props: TextAreaProps): JSX.Element {
  const id = useId();
  const control = useRef<HTMLTextAreaElement | null>(null);
  const maxRows = props.maxRows ?? 10;

  const resize = useCallback((): void => {
    const node = control.current;
    if (node === null) return;
    // jsdom computes no layout, so every measurement here is 0 and the branch below is inert in tests.
    // That is the honest position: this is a rendering behaviour and a DOM test cannot see it.
    const lineHeight = Number.parseFloat(window.getComputedStyle(node).lineHeight);
    node.style.height = "auto";
    const ceiling = Number.isFinite(lineHeight) ? lineHeight * maxRows : 0;
    node.style.height = ceiling > 0 ? `${Math.min(node.scrollHeight, ceiling)}px` : `${node.scrollHeight}px`;
    node.style.overflowY = ceiling > 0 && node.scrollHeight > ceiling ? "auto" : "hidden";
  }, [maxRows]);

  // On the value as well as on mount: the box is filled programmatically when a form is seeded from a
  // record, and a height set only on keystroke would leave that arriving text clipped.
  useEffect(resize, [resize, props.value]);

  return (
    <FieldFrame
      id={id}
      label={props.label}
      {...(props.hint === undefined ? {} : { hint: props.hint })}
      {...(props.error === undefined ? {} : { error: props.error })}
      {...(props.required === undefined ? {} : { required: props.required })}
    >
      {(described) => (
        <textarea
          id={id}
          ref={control}
          // The same class the single-line control wears: one skin, so the two cannot drift apart, and
          // the coarse-pointer 16px floor in the stylesheet already names `textarea` alongside it.
          className="input"
          value={props.value}
          rows={props.rows ?? 3}
          // Horizontal resize would let a reader drag the control out of the single-column form (§3.3).
          style={{ resize: "vertical" }}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => props.onChange(event.target.value)}
          aria-invalid={props.error === undefined ? undefined : true}
          aria-describedby={described}
          disabled={props.disabled ?? false}
          required={props.required ?? false}
          {...(props.maxLength === undefined ? {} : { maxLength: props.maxLength })}
          {...(props.placeholder === undefined ? {} : { placeholder: props.placeholder })}
          {...(props.onBlur === undefined ? {} : { onBlur: props.onBlur })}
        />
      )}
    </FieldFrame>
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps {
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
  readonly hint?: string;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  /** Hides the visible label without removing it from the accessibility tree (toolbars, sort bars). */
  readonly labelHidden?: boolean;
}

export function Select(props: SelectProps): JSX.Element {
  const id = useId();
  const control = (described: string | undefined): ReactNode => (
    <select
      id={id}
      className="select"
      value={props.value}
      onChange={(event: ChangeEvent<HTMLSelectElement>) => props.onChange(event.target.value)}
      aria-describedby={described}
      disabled={props.disabled ?? false}
    >
      {props.options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled ?? false}>
          {option.label}
        </option>
      ))}
    </select>
  );

  if (props.labelHidden === true) {
    return (
      <div className="field">
        <label className="visually-hidden" htmlFor={id}>
          {props.label}
        </label>
        {control(undefined)}
      </div>
    );
  }

  return (
    <FieldFrame
      id={id}
      label={props.label}
      {...(props.hint === undefined ? {} : { hint: props.hint })}
      {...(props.error === undefined ? {} : { error: props.error })}
      {...(props.required === undefined ? {} : { required: props.required })}
    >
      {control}
    </FieldFrame>
  );
}

export interface CheckboxProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly hint?: string;
}

/** The label is part of the hit target — a 16px box alone is not a target on a phone. */
export function Checkbox(props: CheckboxProps): JSX.Element {
  const id = useId();
  const hintId = props.hint === undefined ? undefined : `${id}-hint`;
  return (
    <div className="field">
      <label className="checkbox" htmlFor={id}>
        <input
          id={id}
          className="checkbox__box"
          type="checkbox"
          checked={props.checked}
          disabled={props.disabled ?? false}
          aria-describedby={hintId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => props.onChange(event.target.checked)}
        />
        <span className="checkbox__label">{props.label}</span>
      </label>
      {props.hint !== undefined ? (
        <p className="field__hint" id={hintId}>
          {props.hint}
        </p>
      ) : null}
    </div>
  );
}

export interface RadioOption {
  readonly value: string;
  readonly label: string;
  /** One line under the option. For a choice between two ways of doing a thing, this is what explains it. */
  readonly hint?: string;
}

export interface RadioGroupProps {
  readonly legend: string;
  /** There is deliberately no "nothing selected" state: §3.2 requires a default or an explicit none. */
  readonly value: string;
  readonly options: readonly RadioOption[];
  readonly onChange: (value: string) => void;
  readonly hint?: string;
}

/**
 * A `fieldset`, a `legend`, and real radio inputs — design system §3.2.
 *
 * Written here rather than hand-rolled at the one call site that needs it, which is the drift the design
 * system exists to prevent: a screen that invents its own radios gets the roles, the keyboard behaviour
 * (arrow keys move within a group, Tab leaves it) and the touch target subtly wrong, and every screen
 * after it copies whichever version it found first. The native control has all of that already.
 */
export function RadioGroup(props: RadioGroupProps): JSX.Element {
  const id = useId();
  const hintId = props.hint === undefined ? undefined : `${id}-hint`;
  return (
    <fieldset className="field fieldgroup">
      <legend className="field__label">{props.legend}</legend>
      {props.options.map((option) => {
        const optionId = `${id}-${option.value}`;
        const optionHintId = option.hint === undefined ? undefined : `${optionId}-hint`;
        return (
          <div className="fieldgroup__option" key={option.value}>
            {/* The label wraps the input, so the words are part of the hit target rather than beside it. */}
            <label className="radio" htmlFor={optionId}>
              <input
                id={optionId}
                className="radio__dot"
                type="radio"
                name={id}
                value={option.value}
                checked={props.value === option.value}
                aria-describedby={[optionHintId, hintId].filter((v) => v !== undefined).join(" ") || undefined}
                onChange={() => props.onChange(option.value)}
              />
              <span className="radio__label">{option.label}</span>
            </label>
            {option.hint === undefined ? null : (
              <p className="field__hint radio__hint" id={optionHintId}>
                {option.hint}
              </p>
            )}
          </div>
        );
      })}
      {props.hint === undefined ? null : (
        <p className="field__hint" id={hintId}>
          {props.hint}
        </p>
      )}
    </fieldset>
  );
}

export interface MultiSelectProps {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly selected: readonly string[];
  readonly onChange: (selected: readonly string[]) => void;
  readonly hint?: string;
  readonly disabled?: boolean;
  readonly emptyLabel?: string;
}

/**
 * A disclosure over a group of checkboxes, rather than a `<select multiple>`.
 *
 * The native control is unusable on touch and unreadable past four options, and every "combobox" library
 * that replaces it re-implements focus, roles and keyboard handling — usually incorrectly. A button, a
 * panel, and real checkboxes get the semantics for free: each option is already a labelled control, the
 * group is already announced, and the only behaviour left to write is closing the panel.
 */
export function MultiSelect(props: MultiSelectProps): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const selected = useMemo(() => new Set(props.selected), [props.selected]);

  // The shared primitive, which is where this control GAINED focus return on Escape — it had click-outside
  // and Escape and dropped focus on the floor, which §5.1 forbids. See `useDismissable`.
  const close = useCallback((): void => setOpen(false), []);
  useDismissable(open, close, trigger, panel);

  const toggle = (value: string): void => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // Order follows the option list rather than click order, so the value reads the same however it was
    // assembled and two equivalent selections are never rendered differently.
    props.onChange(props.options.filter((option) => next.has(option.value)).map((option) => option.value));
  };

  const summary =
    selected.size === 0
      ? (props.emptyLabel ?? "None selected")
      : props.options
          .filter((option) => selected.has(option.value))
          .map((option) => option.label)
          .join(", ");

  return (
    <div className="field">
      <span className="field__label" id={`${id}-label`}>
        {props.label}
      </span>
      <div className="multiselect">
        <button
          ref={trigger}
          type="button"
          className="multiselect__trigger"
          aria-expanded={open}
          aria-labelledby={`${id}-label ${id}-value`}
          disabled={props.disabled ?? false}
          onClick={() => setOpen((previous) => !previous)}
        >
          <span className="multiselect__value" id={`${id}-value`}>
            {summary}
          </span>
          <span aria-hidden="true">{open ? "▲" : "▼"}</span>
        </button>
        {open ? (
          <div className="multiselect__panel" ref={panel} role="group" aria-labelledby={`${id}-label`}>
            {props.options.map((option) => (
              <label className="multiselect__option" key={option.value}>
                <input
                  type="checkbox"
                  className="checkbox__box"
                  checked={selected.has(option.value)}
                  disabled={option.disabled ?? false}
                  onChange={() => toggle(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        ) : null}
      </div>
      {props.hint !== undefined ? <p className="field__hint">{props.hint}</p> : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Badge, Card, Banner
 * ------------------------------------------------------------------------------------------------- */

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";

/** The glyph beside the word: status is never carried by colour alone. */
const TONE_GLYPH: Readonly<Record<Tone, string>> = {
  neutral: "•",
  success: "✓",
  warning: "!",
  danger: "×",
  info: "i",
};

export interface BadgeProps {
  readonly children: ReactNode;
  readonly tone?: Tone;
}

export function Badge({ children, tone = "neutral" }: BadgeProps): JSX.Element {
  return (
    <span className={`badge badge--${tone}`}>
      <span className="badge__glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      {children}
    </span>
  );
}

export interface CardProps {
  readonly title?: string;
  readonly support?: ReactNode;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
  /**
   * An id on the card's own element, so a page can bring it into view or point a label at it.
   *
   * Offered rather than a ref because the callers that need it want `getElementById` and a scroll, and a
   * forwarded ref would put the kit's internals — which element is the heading, which the section — into
   * every page that took one.
   */
  readonly id?: string;
}

export function Card({ title, support, actions, children, id }: CardProps): JSX.Element {
  return (
    <section className="card" {...(id === undefined ? {} : { id })}>
      {title !== undefined || actions !== undefined ? (
        <header className="card__header">
          {title !== undefined ? <h2 className="card__title">{title}</h2> : <span />}
          {actions}
        </header>
      ) : null}
      {support !== undefined ? <p className="card__support">{support}</p> : null}
      {children}
    </section>
  );
}

export interface BannerProps {
  readonly tone?: Exclude<Tone, "neutral" | "success">;
  readonly children: ReactNode;
}

/** Persistent and contextual — placed above the region it concerns, never at the top of the page. */
export function Banner({ tone = "info", children }: BannerProps): JSX.Element {
  return (
    <p className={`banner banner--${tone}`}>
      <span aria-hidden="true">{TONE_GLYPH[tone]}</span>
      <span>{children}</span>
    </p>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * The states beyond the happy path
 * ------------------------------------------------------------------------------------------------- */

export interface SpinnerProps {
  readonly label?: string;
  readonly large?: boolean;
}

export function Spinner({ label = "Loading", large = false }: SpinnerProps): JSX.Element {
  return (
    <span role="status">
      <span className={large ? "spinner spinner--lg" : "spinner"} aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  /** What would appear here, and the action that would produce it. "No results" on its own is a defect. */
  readonly body: ReactNode;
  readonly action?: ReactNode;
}

export function EmptyState({ title, body, action }: EmptyStateProps): JSX.Element {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      <p className="state__body">{body}</p>
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  /** Plain language. Never a stack trace, and never a code on its own. */
  readonly message: string;
  readonly traceId?: string | null;
  readonly onRetry?: () => void;
}

export function ErrorState({ message, traceId, onRetry }: ErrorStateProps): JSX.Element {
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">That did not work</p>
      <p className="state__body">{message}</p>
      {traceId !== undefined && traceId !== null && traceId !== "" ? (
        <p className="state__body mono">Trace {traceId}</p>
      ) : null}
      {onRetry !== undefined ? (
        <Button variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export interface DeniedStateProps {
  readonly title?: string;
  readonly body?: ReactNode;
  /**
   * A way ON, for the refusals that have one — the same slot `EmptyState` carries, and for the same reason.
   *
   * Optional, and it stays optional: most refusals genuinely lead nowhere but back to an administrator, and
   * inventing a destination to avoid a dead end would be worse than the dead end. It exists because since
   * 7 August 2026 a subject can reach no work module at all and still hold the administration console — an
   * auditor, most obviously — and leaving them on a bare denial with the one place they CAN go undrawn is
   * the failure this prop closes.
   */
  readonly action?: ReactNode;
}

/**
 * Distinct from empty and from error, deliberately.
 *
 * Rendering an empty list where the real answer is "not allowed" tells the user their data is gone. It is
 * also the single easiest way for a UI to hide a permissions bug from the people who could report it.
 */
export function DeniedState({
  title = "You do not have access to this",
  body = "Ask an administrator to add you to the group that grants it.",
  action,
}: DeniedStateProps): JSX.Element {
  return (
    <div className="state state--denied" role="status">
      <p className="state__title">{title}</p>
      <p className="state__body">{body}</p>
      {action}
    </div>
  );
}

export interface LoadingStateProps {
  readonly label?: string;
  readonly rows?: number;
}

/** A skeleton that keeps the container's height, so nothing jumps when the real content lands. */
export function LoadingState({ label = "Loading", rows = 4 }: LoadingStateProps): JSX.Element {
  return (
    <div className="state" aria-busy="true" aria-live="polite">
      <Spinner label={label} large />
      <div className="stack" style={{ width: "100%" }}>
        {Array.from({ length: rows }, (_unused, index) => (
          <div className="skeleton" key={index} />
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Modal
 * ------------------------------------------------------------------------------------------------- */

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly onClose: () => void;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
  /** `alertdialog` for a confirmation; the default is right for everything else. */
  readonly role?: "dialog" | "alertdialog";
  /** Set where the dialog holds unsaved input: a scrim click then asks rather than discarding silently. */
  readonly confirmDismiss?: () => boolean;
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  actions,
  role = "dialog",
  confirmDismiss,
}: ModalProps): JSX.Element | null {
  const dialog = useRef<HTMLDivElement | null>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const requestClose = useCallback((): void => {
    if (confirmDismiss !== undefined && !confirmDismiss()) return;
    onClose();
  }, [confirmDismiss, onClose]);

  /**
   * The dismissal is read through a ref so the focus effect below can depend on `open` ALONE.
   *
   * It used to depend on `requestClose`, which is rebuilt whenever a caller passes an inline `onClose` or
   * `confirmDismiss` — and a `confirmDismiss` that consults live form state (the only kind worth having)
   * changes on every keystroke. The effect then re-ran per render and re-focused the dialog's first
   * control, so a form inside a modal accepted its first character and silently dropped the rest. Nothing
   * failed; the field simply would not fill in.
   */
  const closeRef = useRef(requestClose);
  useEffect(() => {
    closeRef.current = requestClose;
  }, [requestClose]);

  useEffect(() => {
    if (!open) return;

    // Whatever opened the dialog gets focus back when it closes — including when it closes because the
    // component unmounted, which is why the restore lives in the effect's cleanup rather than in onClose.
    restoreTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = dialog.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      // The trap. Without it, Tab walks out of the dialog into the page behind, which is inert to the eye
      // and entirely reachable by keyboard.
      const focusable = Array.from(node?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (focusable.length === 0) return;
      const firstNode = focusable[0];
      const lastNode = focusable[focusable.length - 1];
      if (firstNode === undefined || lastNode === undefined) return;

      const active = document.activeElement;
      if (event.shiftKey && active === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && active === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = overflow;
      restoreTo.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="scrim" onClick={requestClose} aria-hidden="true" />
      <div
        className="modal"
        ref={dialog}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
      >
        {/* The sheet's grip, shown only below --bp-md by the stylesheet. Decorative: dismissal is always
            also available by Escape and by the scrim, never by swipe alone. */}
        <span className="modal__grip" aria-hidden="true" />
        <h2 className="modal__title" id={titleId}>
          {title}
        </h2>
        {description !== undefined ? (
          <p className="modal__description" id={descriptionId}>
            {description}
          </p>
        ) : null}
        <div className="modal__body">{children}</div>
        <div className="modal__actions">{actions}</div>
      </div>
    </>,
    document.body,
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Toast — one viewport, above modals, errors never auto-dismiss.
 * ------------------------------------------------------------------------------------------------- */

export interface ToastOptions {
  readonly tone?: Exclude<Tone, "neutral" | "warning">;
  readonly detail?: string;
}

interface ToastRecord {
  readonly id: number;
  readonly message: string;
  readonly tone: "success" | "danger" | "info";
  readonly detail?: string;
}

interface ToastApi {
  readonly show: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Auto-dismiss: 5s for the informational ones. An error stays until it is dismissed — it is the outcome
 *  of something the user did, and a message missed is an action believed to have succeeded. */
const TOAST_TTL_MS = 5000;
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, options: ToastOptions = {}): void => {
      const tone: ToastRecord["tone"] = options.tone ?? "info";
      const id = nextId.current++;
      setToasts((current) =>
        [
          ...current,
          {
            id,
            message,
            tone,
            ...(options.detail === undefined ? {} : { detail: options.detail }),
          },
        ].slice(-MAX_VISIBLE),
      );
      if (tone !== "danger") window.setTimeout(() => dismiss(id), TOAST_TTL_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-viewport">
        {toasts.map((toast) => (
          <div
            className={`toast toast--${toast.tone === "danger" ? "error" : toast.tone}`}
            key={toast.id}
            role={toast.tone === "danger" ? "alert" : "status"}
          >
            <span aria-hidden="true">{TONE_GLYPH[toast.tone === "danger" ? "danger" : toast.tone]}</span>
            <span className="toast__message">
              {toast.message}
              {toast.detail !== undefined ? <span className="toast__meta">{toast.detail}</span> : null}
            </span>
            <Button variant="ghost" size="sm" label="Dismiss" onClick={() => dismiss(toast.id)}>
              ×
            </Button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Throws outside a provider rather than degrading to silence: a swallowed outcome is worse than a crash. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error("useToast must be used within a ToastProvider");
  return api;
}

/* ---------------------------------------------------------------------------------------------------
 * Table — the capability floor
 * ------------------------------------------------------------------------------------------------- */

export type SortDirection = "ascending" | "descending";

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  /** Decides the comparison, not the appearance. A numeric column sorted as text is a defect. */
  readonly type?: ValueType;
  /** Only the four permitted exemptions (avatars, actions, selection, free-form icons) set this false. */
  readonly sortable?: boolean;
  readonly numeric?: boolean;
  /** The sort key, and the value shown in the card transform when `render` is absent. */
  readonly value?: (row: T) => SortableValue;
  readonly render?: (row: T) => ReactNode;
  /** Scanning-only columns are DROPPED in the card transform, never shrunk. */
  readonly hideOnMobile?: boolean;
  /**
   * A small number whose whole value is being COMPARED with its neighbours.
   *
   * In the table this changes nothing. In the card transform these columns are lifted out of the
   * label-and-value list and set as one dense wrapping strip, because the ordinary treatment gives each
   * number a full row of its own and eleven of them turn one run into a screenful — which is the reason
   * the run history was on a horizontal scroll instead, and the reason a phone could not read it.
   *
   * Marking a column here is a claim that its value is SHORT. A sentence set in this strip will not wrap
   * gracefully; it will just be a narrow column of words.
   */
  readonly metric?: boolean;
  /** The metric strip's label. Defaults to `header`, which is often too long once it is a chip. */
  readonly shortHeader?: string;
  /**
   * This column holds controls rather than a value.
   *
   * In the table it changes nothing. In the card transform it moves to the foot of the card and takes the
   * full width, instead of being squeezed into the right-hand 60% a value column gets. Buttons in that
   * column wrapped into a ragged right-aligned stack — a wide primary, two links trailing under it, and a
   * third on a line of its own — which on a phone was the least usable thing on the screen.
   *
   * Declared rather than inferred from `sortable: false`: three of the four permitted unsortable
   * exemptions are ordinary value columns, and lifting those out of the pair list would be wrong.
   */
  readonly actions?: boolean;
}

export interface TableProps<T> {
  /** Describes the table for anyone who cannot see it. Rendered as a visible caption. */
  readonly caption: string;
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  /** The default sort. It is MARKED as the default — an unmarked order reads as arbitrary. */
  readonly defaultSort: { readonly key: string; readonly direction: SortDirection };
  /** Which column titles a card in the mobile transform. Defaults to the first column. */
  readonly titleKey?: string;
  /**
   * The mobile transform, chosen deliberately per table:
   *  - `cards`   — for rows with more than about four meaningful columns (jobs, users).
   *  - `scroll`  — for genuinely tabular, comparative data (run counters).
   */
  readonly mobile?: "cards" | "scroll";
  readonly loading?: boolean;
  readonly error?: { readonly message: string; readonly traceId?: string | null; readonly onRetry?: () => void };
  readonly empty?: { readonly title: string; readonly body: ReactNode; readonly action?: ReactNode };
}

function cellValue<T>(column: Column<T>, row: T): SortableValue {
  return column.value === undefined ? null : column.value(row);
}

export function Table<T>(props: TableProps<T>): JSX.Element {
  const { columns, rows, rowKey, defaultSort, caption } = props;
  const [sort, setSort] = useState<{ key: string; direction: SortDirection }>(defaultSort);
  const isMobile = useIsMobile();
  const mobileMode = props.mobile ?? "cards";

  const sorted = useMemo(() => {
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (column === undefined || column.sortable === false) return rows;
    const factor = sort.direction === "ascending" ? 1 : -1;
    const type = column.type ?? "text";

    // Stability by explicit index: rows that tie keep their underlying order, so a refetch that returns
    // the same rows never reshuffles them under the reader's eye.
    return rows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const compared = compareValues(cellValue(column, left.row), cellValue(column, right.row), type);
        // The empty-grouping verdict from compareValues is deliberately NOT flipped by direction: a
        // missing value is not "smallest", it is absent, and it belongs at the end either way.
        const aEmpty = isEmptyValue(cellValue(column, left.row));
        const bEmpty = isEmptyValue(cellValue(column, right.row));
        if (aEmpty !== bEmpty) return compared;
        return compared === 0 ? left.index - right.index : compared * factor;
      })
      .map((entry) => entry.row);
  }, [columns, rows, sort]);

  /** Ascending → descending → back to the default. Never through an indistinguishable unsorted state. */
  const cycle = (key: string): void => {
    setSort((current) => {
      if (current.key !== key) return { key, direction: "ascending" };
      if (current.direction === "ascending") return { key, direction: "descending" };
      return defaultSort;
    });
  };

  const sortableColumns = columns.filter((column) => column.sortable !== false);
  const titleColumn =
    columns.find((column) => column.key === props.titleKey) ?? columns[0] ?? undefined;

  if (props.loading === true) return <LoadingState label={`Loading ${caption}`} />;
  if (props.error !== undefined) {
    return (
      <ErrorState
        message={props.error.message}
        {...(props.error.traceId === undefined ? {} : { traceId: props.error.traceId })}
        {...(props.error.onRetry === undefined ? {} : { onRetry: props.error.onRetry })}
      />
    );
  }
  if (rows.length === 0) {
    const empty = props.empty ?? { title: "Nothing here yet", body: `No ${caption} to show.` };
    return (
      <EmptyState
        title={empty.title}
        body={empty.body}
        {...(empty.action === undefined ? {} : { action: empty.action })}
      />
    );
  }

  // Strategy A — card per row. The header row is gone, so the sort REAPPEARS as a visible control above
  // the cards. Dropping to cards is a change of layout, not a withdrawal of capability.
  //
  // The split is computed once per render rather than inside the row map: it depends only on the column
  // definitions, and re-deriving it for every row is work proportional to rows × columns for an answer
  // that cannot differ between them.
  const shownOnMobile = columns.filter(
    (column) => column !== titleColumn && column.hideOnMobile !== true,
  );
  const pairColumns = shownOnMobile.filter(
    (column) => column.metric !== true && column.actions !== true,
  );
  const metricColumns = shownOnMobile.filter((column) => column.metric === true);
  const actionColumns = shownOnMobile.filter((column) => column.actions === true);

  if (isMobile && mobileMode === "cards") {
    return (
      <div className="stack">
        <div className="table-sortbar">
          <Select
            label={`Sort ${caption} by`}
            value={sort.key}
            options={sortableColumns.map((column) => ({ value: column.key, label: column.header }))}
            onChange={(key) => setSort((current) => ({ key, direction: current.direction }))}
          />
          <Button
            variant="secondary"
            onClick={() =>
              setSort((current) => ({
                key: current.key,
                direction: current.direction === "ascending" ? "descending" : "ascending",
              }))
            }
            label={`Sort ${sort.direction === "ascending" ? "descending" : "ascending"}`}
          >
            {sort.direction === "ascending" ? "↑ A–Z" : "↓ Z–A"}
          </Button>
        </div>
        <ul className="table-cards" aria-label={caption}>
          {sorted.map((row) => (
            <li className="table-card" key={rowKey(row)}>
              {titleColumn === undefined ? null : (
                <p className="table-card__title">{renderCell(titleColumn, row)}</p>
              )}
              {pairColumns.map((column) => (
                <div className="table-card__pair" key={column.key}>
                  <span className="table-card__key">{column.header}</span>
                  <span className="table-card__value">{renderCell(column, row)}</span>
                </div>
              ))}
              {metricColumns.length === 0 ? null : (
                // A `<dl>` rather than more pairs: these are still label-and-value, so the association has
                // to survive for a screen reader even though the layout has stopped looking like rows.
                <dl className="table-card__metrics">
                  {metricColumns.map((column) => (
                    <div className="metric" key={column.key}>
                      <dt className="metric__key">{column.shortHeader ?? column.header}</dt>
                      <dd className="metric__value">{renderCell(column, row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {actionColumns.map((column) => (
                // Full width, at the foot, with the header as the accessible name rather than a visible
                // label — "Actions" beside the buttons costs a third of the row and says nothing the
                // buttons do not.
                <div className="table-card__actions" key={column.key} aria-label={column.header}>
                  {renderCell(column, row)}
                </div>
              ))}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Strategy B — the table itself, scrolling inside its own container. The page never scrolls sideways.
  return (
    <div className="table-wrap">
      <table className="table">
        <caption>
          {caption} — sorted by{" "}
          {columns.find((column) => column.key === sort.key)?.header ?? sort.key}
          {sort.direction === "ascending" ? ", ascending" : ", descending"}
          {sort.key === defaultSort.key && sort.direction === defaultSort.direction ? " (default)" : ""}
        </caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const active = column.key === sort.key;
              const ariaSort: "ascending" | "descending" | "none" = active ? sort.direction : "none";
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={column.numeric === true ? "numeric" : undefined}
                  // On the <th> itself: assistive technology announces the column, not the button in it.
                  aria-sort={column.sortable === false ? undefined : ariaSort}
                >
                  {column.sortable === false ? (
                    <span className="table__head-static">{column.header}</span>
                  ) : (
                    <button type="button" className="table__sort" onClick={() => cycle(column.key)}>
                      {column.header}
                      <span className="table__sort-mark" aria-hidden="true">
                        {active ? (sort.direction === "ascending" ? "▲" : "▼") : "↕"}
                      </span>
                    </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td key={column.key} className={column.numeric === true ? "numeric" : undefined}>
                  {renderCell(column, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isEmptyValue(value: SortableValue): boolean {
  return value === null || value === undefined || value === "";
}

function renderCell<T>(column: Column<T>, row: T): ReactNode {
  if (column.render !== undefined) return column.render(row);
  const value = cellValue(column, row);
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

/* ---------------------------------------------------------------------------------------------------
 * Small shared helpers
 * ------------------------------------------------------------------------------------------------- */

export interface PageProps {
  readonly title: string;
  readonly lede?: string;
  readonly actions?: ReactNode;
  readonly measure?: "reading" | "read-wide" | "wide";
  readonly children: ReactNode;
}

/** The page frame: one `<h1>`, a measure chosen deliberately, and the section rhythm. */
export function Page({ title, lede, actions, measure = "wide", children }: PageProps): JSX.Element {
  return (
    <div className={`page page--${measure}`}>
      <div className="page__header">
        <h1>{title}</h1>
        <span className="topbar__spacer" />
        {/*
          The actions are wrapped rather than dropped in loose, so that on a phone they can be moved as
          ONE thing. Bare siblings in a wrapping flex row each wrap independently, which is how a title
          and its two buttons ended up sharing a line three ways with nothing aligned to anything.
        */}
        {actions === undefined ? null : <div className="page__actions">{actions}</div>}
      </div>
      {lede !== undefined ? <p className="page__lede">{lede}</p> : null}
      {children}
    </div>
  );
}

export interface FormActionsProps {
  readonly children: ReactNode;
}

/**
 * The action bar that closes a long form: Cancel and Save, at the END of the thing they act on.
 *
 * ## Why a form's actions are not in the page head, when a list's are
 *
 * `02-patterns.md` §2.1 puts a page's actions in the head and then makes an exception in as many words:
 * *"Where there is exactly one primary action and the page is a long form or list, it SHOULD instead pin
 * to the bottom"* — and `03-components.md` §2 says a form's primary action SHOULD be full-width on mobile
 * or pinned above the bottom navigation. So this is the standard's own preference for this shape of page,
 * not a departure from it.
 *
 * The reason underneath the clause is worth stating, because it is what decides where a future action
 * goes. A LIST's primary action ("Add business unit") is about the page — it is available before you have
 * read anything, and putting it at the foot of two hundred rows would hide it. A FORM's primary action is
 * the *conclusion* of the page: it is only meaningful once the fields above it have been filled, and in
 * the head it sits at the one point in the page where the user has decided nothing yet. On a phone that
 * gap is the whole screen, so Save scrolls away the instant somebody starts typing and the way back to it
 * is to scroll past every field they have just filled.
 *
 * So: list pages keep `Page`'s `actions`; forms end with this. `AdminBusinessUnits` and `AdminAccess` are
 * the former and deliberately unchanged.
 *
 * ## What this is NOT
 *
 * It is not sticky, and that is a decision rather than an omission. §2.1's exception says "SHOULD" and
 * names the single-primary-action case; a pinned bar would additionally have to clear the phone's bottom
 * tab bar and its safe-area inset (§2.2: *"Bottom padding MUST clear any fixed bottom bar plus its
 * safe-area inset"*), and getting that wrong lays a button over the last field of the form — which is the
 * defect the sibling app's ADR 0133 records having shipped, in the one display mode nothing tests.
 * Full-width on mobile is the other option the standard offers and is the one taken here.
 *
 * It is also not a place for an action that acts IMMEDIATELY. Delete, Reassign and Update now belong to
 * `JobAdmin`, below this, because they are decisions the server re-checks when pressed rather than things
 * Save commits. Mixing them would put a destructive control a thumb's width from the primary one, which
 * `03-components.md` §2 forbids outright.
 */
export function FormActions({ children }: FormActionsProps): JSX.Element {
  // `role="group"` rather than a bare div: a screen reader announces the set, and the label says what
  // pressing anything here acts ON — which is not obvious after scrolling through eight cards of fields.
  return (
    <div className="form-actions" role="group" aria-label="Save or discard this update">
      {children}
    </div>
  );
}

/** Keyboard handling that survives an IME: Enter mid-composition is a word choice, not a submission. */
export function onEnter(handler: () => void) {
  return (event: ReactKeyboardEvent): void => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    handler();
  };
}
